import CTNCheckBox from '@ctn/ui/components/ctn-checkbox';
import CTNDropdown from '@ctn/ui/components/ctn-dropdown';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNInputSwitch from '@ctn/ui/components/ctn-input-switch';
import { type IconInfo } from '@ctn/ui/types/generic';
import { useMemo, useState } from 'react';
import {useFieldArray, type UseFormReturn } from 'react-hook-form';

import { useGlobalContext } from '@/context/global.context';

import { Cleaners } from '@/helpers/Cleaners/fieldDataCleaner';

import { CheckboxGroup } from './CheckboxGroup';
import { type NotificacionTramiteForm, type IUserForm } from './config';

type UserFormProps = {
	readonly methods: UseFormReturn<IUserForm>;
	readonly show?: {
		activo: boolean;
	};
	readonly disabled?: {
		username: boolean;
		activo: boolean;
	};
	readonly onChange: {
		activo: (value: Array<string | number>) => void;
	};
	readonly isSameUser: boolean;
};

const UserForm = ({ methods, show, disabled, onChange, isSameUser }: UserFormProps) => {
	const {
		user,
		maestrosGeneric: { rol },
	} = useGlobalContext();


	const {
		formState: { errors },
		control,
		resetField,
		watch,
		setValue,
	} = methods;
	const [version, setVersion] = useState(0);

	const serviciosNotificaciones = methods.watch('serviciosNotificaciones');
	const email = methods.watch('email');

	const { fields, replace, update } = useFieldArray({
		control,
		name: 'serviciosNotificaciones',
	});

	const resetSubItems = (nameSubItems: `serviciosNotificaciones.${number}.listaTipoNotificacion`) => {
		const currentValue = watch(nameSubItems);
		const resetValues = currentValue.map((data) => ({ ...data, value: [] }));
		resetField(nameSubItems, {
			defaultValue: resetValues,
		});
	};

	const handleGroupClick = () => {
		const allSelected = isAllSelected;
		const nuevosServicios: NotificacionTramiteForm[] = serviciosNotificaciones.map((servYnoti) => ({
			...servYnoti,
			tipoTramite: {
				...servYnoti.tipoTramite,
				value: allSelected ? [] : ['true']
			},
			listaTipoNotificacion: servYnoti.listaTipoNotificacion.map((lista) => ({
				...lista,
				value: allSelected ? [] : ['true']
			}))
		}));
		replace(nuevosServicios);
		setVersion(v => v + 1);
	};

	const handleItemClick = (id: number) => {
		const allSelected = isTramiteNotificationsSelected(id);
		const index = serviciosNotificaciones.findIndex(s => s.tipoTramite.id === id);

		if (index !== -1) {
			const tramiteOriginal = serviciosNotificaciones[index];
			const tramiteActualizado: NotificacionTramiteForm = {
				...tramiteOriginal,
				tipoTramite: {
					...tramiteOriginal.tipoTramite,
					value: ['true']
				},
				listaTipoNotificacion: tramiteOriginal.listaTipoNotificacion.map((noti) => ({
					...noti,
					value: allSelected ? [] : ['true']
				}))
			};

			update(index, tramiteActualizado);
			setVersion(v => v + 1);
		}
	};

	const isTramiteNotificationsSelected = (index: number) => {
		const tramite = serviciosNotificaciones.find((it) => it.tipoTramite.id === index);
		if (!tramite) return false;
		const areHijosSelected = tramite.listaTipoNotificacion.every(noti => 
			noti.value.includes('true')
		);

		return areHijosSelected;
	};

	const isAllSelected = useMemo(() => {
		if (!serviciosNotificaciones || serviciosNotificaciones.length === 0) return false;
		return serviciosNotificaciones.every((item) => isTramiteNotificationsSelected(item.tipoTramite.id));
	}, [serviciosNotificaciones]);

	const iconInfoEmail: IconInfo = {
		color: 'blue-50',
		icon: 'info',
		tooltip: {
			content: 'Si no activa la opción de recibir notificaciones en esta dirección de correo electrónico, esta dirección solo se utiliza para comunicar el alta del usuario.',
			maxWidth: 230,
			place: 'top'
		}
	};

	return (
		<form className="[ c-pnp-form-group ]">
			<fieldset>
				<div className="o-splitter">
					<legend className="c-pnp-heading-h4">Datos del usuario</legend>
					{show?.activo && !isSameUser && (
						<CTNInputSwitch
							noFeedBack
							control={control}
							name="activo"
							items={[{ label: 'Activo', value: 'true', disabled: disabled?.activo }]}
							onChange={onChange?.activo}
						/>
					)}
				</div>
				<div className="[ o-grid ] [ u-mt-24 ]">
					<div className="o-grid__row">
						<CTNInput
							required
							disabled={disabled?.username}
							type="text"
							maxLength={50}
							classNameField="o-grid__col-12 o-grid__col-4@portrait"
							name="username"
							label="Nombre de usuario"
							control={control}
							error={errors.username?.message}
							onBlur={(e) => {
								const limpio = Cleaners.sinEspacios(e.target.value);
								e.target.value = limpio;
								setValue('username', limpio);
							}}
							onChange={(e) => {
								const limpio = Cleaners.sinEspacios(e.target.value);
								e.target.value = limpio;
								setValue('username', limpio);
							}}
						/>
					</div>
					<div className="o-grid__row">
						<CTNInput
							required
							type="text"
							maxLength={100}
							classNameField="o-grid__col-12 o-grid__col-4@portrait"
							name="nombre"
							label="Nombre"
							control={control}
							error={errors.nombre?.message}
							onBlur={(e) => {
								const limpio = Cleaners.texto(e.target.value);
								e.target.value = limpio;
								setValue('nombre', limpio);
							}}
							onChange={(e) => {
								const limpio = Cleaners.texto(e.target.value);
								e.target.value = limpio;
								setValue('nombre', limpio);
							}}
						/>
						<CTNInput
							required
							type="text"
							maxLength={100}
							classNameField="o-grid__col-12 o-grid__col-4@portrait"
							name="apellido1"
							label="Primer apellido"
							control={control}
							error={errors.apellido1?.message}
							onBlur={(e) => {
								const limpio = Cleaners.texto(e.target.value);
								e.target.value = limpio;
								setValue('apellido1', limpio);
							}}
							onChange={(e) => {
								const limpio = Cleaners.texto(e.target.value);
								e.target.value = limpio;
								setValue('apellido1', limpio);
							}}
						/>
						<CTNInput
							type="text"
							maxLength={100}
							classNameField="o-grid__col-12 o-grid__col-4@portrait"
							name="apellido2"
							label="Segundo apellido"
							control={control}
							error={errors.apellido2?.message}
							onBlur={(e) => {
								const limpio = Cleaners.texto(e.target.value);
								e.target.value = limpio;
								setValue('apellido2', limpio);
							}}
							onChange={(e) => {
								const limpio = Cleaners.texto(e.target.value);
								e.target.value = limpio;
								setValue('apellido2', limpio);
							}}
						/>
					</div>
					<div className="o-grid__row">
						<CTNInput
							required
							type="text"
							classNameField="o-grid__col-12 o-grid__col-5@portrait"
							name="email"
							maxLength={100}
							label="Correo electrónico"
							control={control}
							error={errors.email?.message}
							iconField={iconInfoEmail}
						/>
						<CTNInput
							required
							type="text"
							classNameField="o-grid__col-12 o-grid__col-3@portrait"
							name="telefono"
							label="Teléfono"
							control={control}
							error={errors.telefono?.message}
							onBlur={(e) => {
								const limpio = Cleaners.numerico(e.target.value);
								e.target.value = limpio;
								setValue('telefono', limpio);
							}}
							onChange={(e) => {
								const limpio = Cleaners.numerico(e.target.value);
								e.target.value = limpio;
								setValue('telefono', limpio);
							}}
						/>
						<CTNDropdown
							required
							disabled={isSameUser}
							placeholder="Seleccionar"
							classNameField="o-grid__col-12 o-grid__col-4@portrait"
							name="rol"
							options={rol?.map((value) => ({
								label: value.descripcion,
								value: String(value.id),
							}))}
							label="Tipo"
							control={control}
							error={errors.rol?.message}
						/>
					</div>
				</div>
			</fieldset>
			<CTNCheckBox
				classNameField="[ c-pnm-container-politica__checkbox ]"
				name="envioNotificacionMail"
				items={[
					{
						label: 'Recibir notificaciones en esta dirección de correo electrónico',
						disabled: Boolean(!email) || Boolean(errors.email?.message),
						value: '1',
					},
				]}
				noFeedBack={false}
				control={control}
			/>
			<h4 className="c-pnp-heading-h4 c-div-group">
				<p>Seleccione los servicios activos y notificaciones para este usuario</p>
			</h4>
			<CheckboxGroup key={version} title="Servicios y notificaciones" labelButton={isAllSelected ? 'Deseleccionar todo' : 'Seleccionar todo'} onButtonClick={handleGroupClick}>
				{fields.map(({ tipoTramite, listaTipoNotificacion }, indexTramite) => {
					const isExpanded = serviciosNotificaciones[indexTramite]?.tipoTramite?.value?.includes('true');
					const estadoTramite = user?.listaTramitesEstadoContratacion.find((it) => it.tipoTramite.id === tipoTramite.id);

					if (!estadoTramite?.contratado) return null;

					return (
						<CheckboxGroup.Item
							key={tipoTramite.id}
							control={control}
							name={`serviciosNotificaciones.${indexTramite}.tipoTramite.value`}
							label={tipoTramite.descripcion}
							value="true"
							isExpanded={isExpanded}
							labelButton={isTramiteNotificationsSelected(tipoTramite.id) ? 'Deseleccionar todo' : 'Seleccionar todo'}
							onChange={() =>resetSubItems(`serviciosNotificaciones.${indexTramite}.listaTipoNotificacion`)}
							onButtonClick={() => handleItemClick(tipoTramite.id)}
						>
							{listaTipoNotificacion.map(({ id, descripcion }, indexNotificacion) => (
								<CheckboxGroup.SubItem
									key={id}
									control={control}
									name={`serviciosNotificaciones.${indexTramite}.listaTipoNotificacion.${indexNotificacion}.value`}
									label={descripcion}
									value="true"
								/>
							))}
						</CheckboxGroup.Item>
					);
				})}
			</CheckboxGroup>
		</form>
	);
};

export default UserForm;
